import { Alert, Grid, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import pick from 'lodash/pick';
import { useCallback, useMemo, useState } from 'react';

import { DescriptionList } from '../../../../../../design-system/body-sections/description-list/DescriptionList';
import { DescriptionListItem } from '../../../../../../design-system/body-sections/description-list/DescriptionListItem';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type { SalesCoverageUnderwriterEmployeeDetailFragment } from '../graphql/__generated__';
import { useUpdateUnderwriterEmployee } from '../hooks/useUpdateUnderwriterEmployee';
import type { UserDetailsFormValues } from '../models/UserDetailsSection.model';
import {
  getInitialUserDetailsValues,
  SECTIONS_SIZING,
  USER_DETAIL_FIELD_LABELS,
  userDetailsUpdateValidationSchema,
} from '../models/UserDetailsSection.model';
import { EmployeeUserDetailsFields } from './EmployeeUserDetailsFields';
import { UNDERWRITER_EMPLOYEE_JOB_FUNCTIONS } from './EmployeeUserDetailsFields.model';

export type UserDetailsSectionProps = Readonly<{
  tenantId?: string;
  employee: SalesCoverageUnderwriterEmployeeDetailFragment;
}>;

export function UserDetailsSection({ tenantId, employee }: UserDetailsSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const initialValues = useMemo(() => getInitialUserDetailsValues(employee), [employee]);

  const { updateUnderwriterEmployee, error: errorUserDetail } =
    useUpdateUnderwriterEmployee(tenantId);

  const handleSubmit = useCallback(
    async (values: UserDetailsFormValues) => {
      try {
        const userDetailsValues = pick(values, ['name', 'titles', 'jobFunction', 'department']);
        const employeeRestValues = pick(employee, [
          'status',
          'address1',
          'address2',
          'email1',
          'email2',
          'phoneNumber1',
          'phoneNumber2',
          'faxNumber1',
          'faxNumber2',
        ]);

        if (!employee.employeeKey) {
          throw new Error('Employee key is missing.');
        }

        await updateUnderwriterEmployee({
          employeeKeyOrId: employee.employeeKey,
          input: {
            ...employeeRestValues,
            ...userDetailsValues,
          },
        });
        setIsEditing(false);
        SnackbarManager.success('User details updated successfully.');
      } catch {
        SnackbarManager.error('An error occurred while updating user details.');
      }
    },
    [employee, updateUnderwriterEmployee]
  );

  const formik = useFormik<UserDetailsFormValues>({
    initialValues,
    validationSchema: userDetailsUpdateValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    formik.resetForm();
  }, [formik]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <FormikProvider value={formik}>
      <Grid item {...SECTIONS_SIZING}>
        <Section
          title="User Details"
          data-testid="user-details-section"
          actions={[
            <PageGridSectionActions
              key="page-grid-section-actions"
              isSubmitting={formik.isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={handleCancel}
              onSave={handleSave}
              canEdit
            />,
          ]}
          slotProps={{ content: { gap: 1 } }}
        >
          {errorUserDetail && <Alert severity="error">{errorUserDetail.message}</Alert>}
          {isEditing ? (
            <EmployeeUserDetailsFields
              disableEmployeeKey
              autoFocusNameField
              data-testid="employee-user-details-fields"
            />
          ) : (
            <DescriptionList>
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.employeeKey}
                value={employee.employeeKey}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.name}
                value={defaultTo(employee.name, '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.titles}
                value={defaultTo(employee.titles?.join(', '), '-')}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.jobFunction}
                value={defaultTo(
                  UNDERWRITER_EMPLOYEE_JOB_FUNCTIONS[employee.jobFunction ?? ''],
                  '-'
                )}
              />
              <DescriptionListItem
                label={USER_DETAIL_FIELD_LABELS.department}
                value={defaultTo(employee.department, '-')}
              />
            </DescriptionList>
          )}
        </Section>
      </Grid>
    </FormikProvider>
  );
}
